/**
 * Spanish copy.
 *
 * Lifted from "05 Landing Page.dc.html". It is written in Spanish, not
 * translated from the English: the vocabulary is the real one people use here
 * — quincena, corte, MSI, buró, aire — without quote marks and without
 * explaining it as if it were exotic.
 *
 * Neutral Latin American Spanish using "tú" throughout. Mexico is the home
 * market, not a branch.
 *
 * "TwoCoins" is never translated. Neither is "Plus", the plan name. You may say
 * "dos monedas" when explaining the idea, but not as the brand name.
 *
 * Typed as `Dictionary`, so if a key is added to en.ts and forgotten here, the
 * build fails instead of shipping an English string into the Spanish site.
 *
 * Note: for the legal notices, the Spanish version is the binding one for
 * people in Mexico (a consumer contract there is only valid in Spanish, LFPC
 * art. 85) and the English one everywhere else. That is stated on the page
 * itself and in docs/legal.html.
 */
import type { Dictionary } from './types';

const es: Dictionary = {
  meta: {
    title: 'TwoCoins — app de presupuesto que entiende tu quincena',
    description:
      'Cuánto puedes gastar hasta tu quincena, con el dinero que sí tienes: tarjetas, meses sin intereses y fechas de corte. Sin tu banco. 30 días gratis.',
  },

  nav: {
    how: 'Cómo funciona',
    desktop: 'Escritorio',
    gap: 'El hueco',
    features: 'Funciones',
    pricing: 'Precios',
    faq: 'Preguntas',
    cta: 'Abrir TwoCoins',
    skipToContent: 'Ir al contenido',
    menu: 'Menú',
    closeMenu: 'Cerrar menú',
    languageLabel: 'Idioma',
  },

  hero: {
    badge: 'Hecha para México',
    headline: 'Cuánto puedes gastar hasta tu quincena, con el dinero que de verdad tienes.',
    body: 'Tu banco te muestra lo que hay. No lo que debes a la tarjeta, la renta que sale el 1 ni la quincena que cae el 15. TwoCoins junta las cuatro cosas en un solo número que te alcanza hasta tu próximo pago, y te avisa antes de que un pago caiga antes que tu sueldo. Con meses sin intereses y fechas de corte, como funciona el crédito aquí.',
    ctaPrimary: 'Abrir TwoCoins',
    ctaSecondary: 'Ver cómo funciona',
    ctaNote:
      'Abre en este navegador. Treinta días de la app completa, sin tarjeta, sin instalar si no quieres.',
    trust: ['30 días gratis, sin tarjeta', 'Nada que descargar', 'Sin acceso a tu banco'],
    deskCap: {
      k: 'En la laptop',
      v: 'Captura una semana de gastos con teclado, en unos cuatro minutos.',
    },
    phoneCap: {
      k: 'En tu celular',
      v: 'Revisa lo libre hasta tu quincena y regístralo en dos toques, con o sin señal.',
    },
  },

  pwa: {
    kicker: 'Cómo funciona',
    title: 'Es un sitio web. En tu celular y en tu laptop, hasta que decides que sea una app.',
    body: 'No hay tienda de apps, porque no hace falta. Abres el enlace y la app completa ya está corriendo. La agregas a tu pantalla de inicio cuando la quieras a un toque, y deja de verse como una pestaña del navegador.',
    steps: [
      {
        n: '1',
        k: 'Abre el enlace',
        v: 'La app completa, corriendo en tu navegador. No es un demo, ni una cuenta de prueba, ni un video de una app.',
        art: 'twocoins.app',
        artK: 'Celular, laptop, lo que tenga navegador',
      },
      {
        n: '2',
        k: 'Recibe tu número',
        v: 'Cinco preguntas cortas: dónde vives, cada cuánto y cuánto te pagan, lo que tienes en el banco y en las tarjetas, lo que se repite. Noventa segundos después tienes una cifra real.',
        art: '90 segundos',
        artK: 'Antes de pedirte nada',
      },
      {
        n: '3',
        k: 'Instálala, si quieres',
        v: 'Dos toques en el celular, un clic en la laptop. Ícono propio, ventana propia, funciona sin señal y se actualiza sola para que nunca estés en una versión vieja.',
        art: '2 toques',
        artK: 'En celular y en escritorio',
      },
    ],
    footK: 'Por qué importa',
    foot: 'Una app de finanzas que tienes que descargar antes de poder juzgarla te está pidiendo una confianza que todavía no se gana. Esta te sirve en los primeros noventa segundos, con tus datos, en el teléfono que ya traes.',
  },

  desktopSection: {
    kicker: 'En pantalla grande',
    title: 'La misma app, haciendo lo que en el celular es un martirio.',
    body: 'Abre el mismo enlace en una laptop y TwoCoins se acomoda para el teclado. No es una versión recortada ni un tablero aparte: es el mismo registro, los mismos números, la misma cuenta, ordenados para el trabajo que jamás harías con el pulgar.',
    winTitle: 'Movimientos',
    winSub: 'Escribe, Tab para avanzar, Enter guarda y abre el siguiente.',
    cols: ['Fecha', 'Concepto', 'Categoría', 'Cuenta', 'Monto'],
    // Pagado con débito: lo que tienes, y el número, bajan con él.
    draft: { d: '20 ago', n: 'Sushi Roll · Condesa', c: 'Comer fuera', a: 'Débito', v: '385' },
    rows: [
      {
        d: '19 ago',
        n: 'Chedraui',
        c: 'Súper',
        cc: '#2E8256',
        a: 'Débito',
        v: '$1,284',
        commit: false,
      },
      {
        d: '18 ago',
        n: 'Servicios · CFE, agua, internet',
        c: 'Comprometido',
        cc: '#3E2F7A',
        a: 'Débito',
        v: '$1,445',
        commit: true,
      },
      {
        d: '17 ago',
        n: 'Metro · 10 viajes',
        c: 'Transporte',
        cc: '#3A6FC4',
        a: 'Débito',
        v: '$100',
        commit: false,
      },
      {
        d: '16 ago',
        n: 'Zara · chamarra',
        c: 'Compras',
        cc: '#8A4E86',
        a: 'BBVA Azul',
        v: '$733',
        commit: true,
      },
      {
        d: '16 ago',
        n: 'Gasolina · Pemex',
        c: 'Transporte',
        cc: '#3A6FC4',
        a: 'Banorte Oro',
        v: '$920',
        commit: false,
      },
      {
        d: '15 ago',
        n: 'Spotify',
        c: 'Comprometido',
        cc: '#3E2F7A',
        a: 'BBVA Azul',
        v: '$129',
        commit: true,
      },
    ],
    footK: 'Tuyo esta quincena',
    cards: [
      {
        k: 'Captura una semana en cuatro minutos',
        v: 'Un renglón de captura que nunca se cierra: escribes, Tab para avanzar, Enter guarda y abre el siguiente. O pegas renglones del estado de cuenta y acomodas las columnas una sola vez.',
        art: '12 renglones',
        artK: 'en menos de un minuto',
      },
      {
        k: 'Mira cuándo termina cada compromiso',
        v: 'Seis meses en pantalla, una barra por obligación, cada una con el mes en que por fin te suelta. El celular te muestra el total bajando; solo aquí ves cuál se acaba cuándo.',
        art: '$3,160',
        artK: 'liberados para febrero',
      },
      {
        k: 'Gráficas que sí necesitan ancho',
        v: 'La curva de gasto en las dos quincenas, tu semana día por día, qué creció y qué bajó, seis meses de hacia dónde se corren tus categorías, y en qué tarjeta cargar lo de hoy.',
        art: '6 meses',
        artK: 'de tendencia de un vistazo',
      },
    ],
    foot: 'Configúrala en la compu y registra en el celular — o nunca toques el celular: la app de escritorio está completa por sí sola, y nada aquí necesita un segundo dispositivo para terminar.',
  },

  /*
   * La app del inicio, como la dibuja Inicio: la quincena de la muestra
   * mexicana, con la quincena y un cliente que pagan al débito, calculada con
   * la aritmética de la propia app (domain/money.ts). test/site-figures.test.ts
   * corre ese registro por ella y exige cada cifra y fecha de aquí.
   */
  app: {
    greeting: 'Hola, Mariana',
    date: 'Jueves 20 de ago',
    period: '16–31 de agosto · 11 días',
    freeLabel: 'Libre hasta el 31 ago',
    perDay: 'Unos {amount} al día, contando hoy',
    days: 12,
    currency: 'MXN',
    have: 'Tienes',
    owe: 'Debes',
    coming: 'Te entra',
    due: 'Pagos',
    aheadLabel: 'Antes del 31 ago',
    ahead: [
      { d: '22 ago', n: 'Claude Pro', v: -399, icon: 'spark' },
      { d: '25 ago', n: 'Cliente · diseño', v: 6500, icon: 'arrowUp' },
      { d: '27 ago', n: 'Samsung TV · Best Buy', v: -1215, icon: 'tv' },
      { d: '28 ago', n: 'Spotify', v: -129, icon: 'play' },
    ],
    sim: { free: 17007, have: 42600, owe: 30350, coming: 6500, due: 1743, spent: 6100 },
    // Las etiquetas de la app de escritorio, para la ventana del inicio.
    nav: ['Inicio', 'Movimientos', 'Dinero'],
    navMore: ['Tú', 'Ayuda'],
    addExpense: 'Registrar gasto',
    search: 'Buscar',
  },

  marquee: [
    'Meses sin intereses',
    'Fecha de corte',
    'Quincenas',
    'Tu sueldo llega solo',
    'Celular y laptop',
    'Gastos compartidos',
    'Sin acceso a tu banco',
  ],

  gap: {
    kicker: 'El hueco',
    title:
      'Todas las apps de presupuesto fueron diseñadas para el sistema bancario de alguien más.',
    body: 'Te dan un “presupuesto” sacado de un sueldo al mes —sueldo, menos pagos, menos lo que anotaste— sin ver nunca lo que hay en tu cuenta. Y cuentan un refri a 18 MSI como $18,000 gastados en marzo. Así, el número te dice que vas bien la semana en que la tarjeta te vacía el débito, y que vas corto con cuarenta mil en el banco.',
    themTitle: 'Una app genérica',
    them: [
      'Te da un “presupuesto” sacado de tu sueldo, sin ver tu banco.',
      'Cuenta un refri a 18 MSI como $18,000 gastados en un mes.',
      'Te pide la contraseña de tu banca en línea para “ayudarte”.',
      'No tiene idea de que tu corte es el día 12.',
    ],
    us: [
      'Parte de lo que tienes en tus cuentas, menos lo que deben tus tarjetas.',
      'Cuenta la mensualidad, y te muestra las otras 17 que vienen.',
      'Nunca la pide. Escribes la tienda una vez y la recuerda.',
      'Te dice qué tarjeta te da 48 días de aire hoy.',
    ],
  },

  features: {
    kicker: 'Qué hace',
    title: 'Cinco cosas que ninguna otra app de aquí hace bien.',
    items: [
      {
        kicker: 'El número',
        title: 'Lo libre hasta tu quincena, con lo que de verdad hay en tu cuenta.',
        body: 'Casi todas las apps te dan un “presupuesto” sacado de tu sueldo y nunca ven tu banco. TwoCoins parte de lo que tienen tus cuentas, le quita lo que deben tus tarjetas y cada pago que sale antes de que cierre tu ciclo —la renta, tus suscripciones, la mensualidad de tus meses sin intereses, nunca el precio completo—, le suma lo que todavía te entra, y te da un solo número, con cuánto te toca al día. Lo tocas y ves cada línea.',
        artLabel: 'Libre hasta el 31 ago',
        artValue: '$17,007',
        artSum: [
          { k: 'Tienes', v: '$42,600' },
          { k: 'Deben tus tarjetas', v: '−$30,350' },
          { k: 'Te entra', v: '+$6,500' },
          { k: 'Pagos', v: '−$1,743' },
        ],
        artNote: 'Unos $1,417 al día, contando hoy.',
      },
      {
        kicker: 'Saldos al día',
        title: 'Tu sueldo llega solo. Pagar la tarjeta no es gastar dos veces.',
        body: 'Dile a TwoCoins cuándo te pagan y el dinero entra a tu cuenta el día que cae, solo: la quincena, cada viernes, o el viernes antes si tu día de pago cae en domingo. Cada pago fijo que registras se descuenta solo, igual. Pagar la tarjeta mueve dinero del débito a la tarjeta en lugar de contar como gasto, y si tu banco dice otra cosa, corriges el saldo en un paso.',
        artLabel: 'Débito · 25 ago',
        artValue: '+$6,500',
        artNote: 'Lo que te paga tu cliente, en tu cuenta el día que llega. Sin escribir nada.',
      },
      {
        kicker: 'Antes de que te falte',
        title: 'Te avisa cuando un pago cae antes que el sueldo que lo cubre.',
        body: 'La quincena puede cuadrar y aun así dejarte corto dos días. TwoCoins recorre tu dinero día por día —lo que hay en el banco, cada pago en su día, cada día de pago— y si la renta sale antes de que llegue tu sueldo, Inicio te lo dice con la fecha y el monto, cuando todavía puedes mover algo.',
        artLabel: 'Aviso',
        artValue: '$3,300',
        artNote: 'El 26 ago te faltarían $3,300, antes de tu pago del 28 ago.',
      },
      {
        kicker: 'Fecha de corte',
        title: 'La mejor tarjeta hoy es la que acaba de cortar.',
        body: 'TwoCoins conoce el corte y la fecha límite de cada tarjeta. Compra el día después del corte y no lo pagas hasta siete semanas después. Es aire gratis que nadie te explica. También lleva tu límite, para que veas tu uso antes de que le pegue a tu buró.',
        artLabel: 'Mejor tarjeta hoy',
        artValue: '48 días',
        artNote:
          'Banorte Oro acaba de cortar, así que lo de hoy cae en el estado del mes que entra.',
      },
      {
        kicker: 'Gastos compartidos',
        title: 'Comparte la renta, no tu cuenta.',
        body: 'Anota lo que compartes con tu pareja o con quien vives, pague quien pague: tu tarjeta, la suya o mitad y mitad en caja. Registra lo que te da y repartan según lo que cada quien puede ese mes. Tus números cuentan solo tu parte, y lo que adelantas queda fuera de lo libre hasta que regrese. La otra persona no necesita cuenta.',
        artLabel: 'Ana te debe',
        artValue: '$748',
        artSplit: 0.64,
        artNote: 'Reparto 64/36. De la renta, $12,000 son tuyos; Ana ya pagó sus $4,000.',
      },
    ],
  },

  privacy: {
    kicker: 'Privacidad',
    title: 'Nunca te pedimos la contraseña de tu banco. No existe el campo.',
    body: 'Tú escribes lo que gastas, o lo eliges de las tiendas que ya usaste. Toma cuatro segundos y significa que el acceso a tu banco nunca está en ningún lado donde se pueda filtrar. Sin rastrear tu banca, sin intermediarios guardando tus credenciales.',
    body2:
      'No hay nada que descargar. Abre en tu navegador, y la puedes agregar a tu pantalla de inicio cuando la quieras a un toque. Sin señal sigue funcionando, así que un error nunca es un gasto perdido.',
    points: [
      {
        k: 'Cero credenciales',
        v: 'No hay pantalla de banca en línea porque no hay conexión al banco.',
      },
      {
        k: 'En tu cuenta',
        v: 'Tus gastos se guardan en tu cuenta y te siguen a cada dispositivo, con una copia en cada uno para que la app funcione sin conexión. Nunca los vendemos.',
      },
      {
        k: 'Funciona sin conexión',
        v: 'Registra un café en el Metro sin señal. Se guarda y se sincroniza después.',
      },
      {
        k: 'Nada que vender',
        v: 'Sin red de anuncios, sin socios de datos, sin negocio de datos “anonimizados”.',
      },
    ],
  },

  pricing: {
    kicker: 'Precios',
    title: 'Un solo precio. La aplicación completa, en todas tus pantallas.',
    sub: 'No es una app de celular con un sitio pegado. Una suscripción cubre el celular, la tabla de escritorio, la línea de compromisos y las gráficas, en todos los dispositivos que sean tuyos. No hay versión gratis recortada, porque una app que te esconde la mitad de tus compromisos es un demo que te miente: mejor tienes todo treinta días, que son dos quincenas, y no te pedimos tarjeta hasta el final.',

    monthlyName: 'Mensual',
    monthlyPrice: '$199',
    monthlyPer: 'MXN al mes, IVA incluido',
    monthlyBody:
      'Celular y escritorio, todas las funciones, todos tus dispositivos. Cancela el mes que quieras.',

    yearlyName: 'Anual',
    yearlyPrice: '$1,990',
    yearlyPer: 'MXN al año, IVA incluido',
    yearlyBadge: '2 meses gratis',
    yearlyBody:
      'La misma aplicación completa, dos meses más barata — para cuando ya sabes que te quedas.',

    cta: 'Empieza 30 días gratis',

    payK: 'Cuánto vale',
    payTitle: 'Se paga sola la primera vez que evitas pagar un mínimo.',
    payStats: [
      {
        v: '$259',
        k: 'de interés que te cuesta un solo pago mínimo el mes que entra, del ejemplo de esta página',
      },
      {
        v: '$3,160',
        k: 'al mes de vuelta cuando terminan tres planes a meses, si sabes en qué mes es',
      },
      {
        v: '$199',
        k: 'al mes por la aplicación completa — menos que el Netflix que te acaba de encontrar en tu propio registro',
      },
    ],

    inclTitle: 'Cualquier plan es la aplicación completa. No hay nada más arriba que comprar.',
    includes: [
      'Cuentas y tarjetas ilimitadas, con saldos que se ponen al día solos',
      'Meses sin intereses y pagos fijos, cargados solos, con el mes en que cada plan termina',
      'Ciclos quincenales, semanales, mensuales y personalizados',
      'Fecha de corte y fecha límite de cada tarjeta',
      'Créditos y automotriz, con el interés por separado',
      'La tabla de escritorio y la captura con teclado',
      'Tendencias, límites por categoría y comparaciones, y exportar a CSV cuando quieras irte',
      'Funciona sin internet, en celular y laptop, instalada o no',
      'Una suscripción, todos tus dispositivos — no se compra dos veces',
    ],

    note: 'No pedimos tarjeta para empezar los treinta días. Cuando terminan, la app te pregunta si quieres suscribirte, y no se cobra nada si no lo haces. Si cancelas, tus datos se quedan donde están: la app se bloquea, nada se borra, y exportar siempre es un toque.',
  },

  faq: {
    kicker: 'Preguntas',
    title: 'Las que de verdad nos hacen.',
    items: [
      {
        q: '¿Dónde la descargo?',
        a: 'No la descargas. TwoCoins es un sitio que se comporta como app: abres el enlace y ya la estás usando. Si la quieres en tu pantalla de inicio, la agregas en dos toques y abre en pantalla completa, funciona sin internet y se actualiza sola. No hay tienda ni instalación que esperar.',
      },
      {
        q: '¿Se conecta a mi banco?',
        a: 'No, y no pensamos hacerlo. Tú registras tus gastos, lo cual toma unos segundos y deja tus credenciales completamente fuera. El trato es honesto: escribes un poco, y nadie guarda la contraseña de tu banco.',
      },
      {
        q: '¿Qué es exactamente un plan a meses para la app?',
        a: 'Un compromiso con una mensualidad, un día de cargo, los pagos hechos y el mes en que termina. Cuenta la mensualidad —nunca el precio completo— cuando cae antes de que cierre tu ciclo, aparece con su fecha en lo que se paga, y se registra sola ese día, para que no la escribas cada mes.',
      },
      {
        q: '¿Y si soy freelance, me pagan por trabajo o tengo dos trabajos?',
        a: 'Agrega cada forma en que te pagan. Lo de días fijos cuenta en sus días y entra solo a su cuenta — un pago semanal junto a uno mensual, cada uno en su lugar, y un pago que cae en fin de semana se muestra el día en que de verdad llega. Lo que varía cuenta cuando llega, nunca antes, para que el número no gaste trabajo que todavía no te pagan; y si facturas antes de impuestos, lo que apartas se queda apartado. Lo que sobre sigue en tu cuenta el ciclo siguiente, porque el número parte de lo que hay.',
      },
      {
        q: '¿Funciona fuera de México?',
        a: 'Sí. Cambia el mercado a Estados Unidos y la app quita lo que no aplica. El paso de meses desaparece en lugar de fingir que existe, y los ciclos pasan a ser mensuales con fecha de estado de cuenta. Preferimos quitar una función que inventarla.',
      },
      {
        q: '¿Puedo llevar el crédito de mi coche?',
        a: 'Sí. Pago mensual, tasa, pagos hechos, saldo restante y cuánto te está costando el interés cada mes, por separado, porque el interés es la parte que nunca recuperas.',
      },
      {
        q: '¿Puedo compartir gastos con mi pareja o con quien vivo?',
        a: 'Sí. Llevas una cuenta compartida con una persona, y ella no necesita cuenta. Anotas lo que comparten pague quien pague, registras lo que te da, y reparten según lo que cada quien puede ese mes. Tus categorías, límites y lo libre cuentan solo tu parte.',
      },
      {
        q: '¿Puedo empezar de cero sin perder mi cuenta?',
        a: 'Sí. En Datos y privacidad limpias tus registros —todos, o lo anterior a una fecha— y conservas tus cuentas con sus saldos, tarjetas, categorías y compromisos, o empiezas de cero como si la cuenta fuera nueva. Tu plan y tus días de prueba se quedan en ambos casos.',
      },
      {
        q: '¿Hay versión gratis?',
        a: 'No, y es a propósito. Toda versión gratis funciona escondiéndote algo: una segunda cuenta, la línea de meses sin intereses, el ciclo que sí coincide con tu quincena. Y media foto de tu dinero es peor que ninguna, porque te la crees. Mejor tienes la app completa treinta días, que son dos quincenas, sin darnos tarjeta.',
      },
      {
        q: '¿Qué pasa si dejo de pagar?',
        a: 'La app se bloquea y no se borra nada. Tus tarjetas, categorías e historial siguen guardados en tu cuenta, y exportar a CSV sigue funcionando. Si vuelves a pagar, todo sigue igual que como lo dejaste.',
      },
      {
        q: '¿Quién la está haciendo?',
        a: 'Build Passing. Equipo pequeño, sin inversionistas pidiéndonos una pestaña de cripto.',
      },
    ],
  },

  finalCta: {
    title: 'Descubre qué es realmente tuyo hasta tu quincena.',
    body: 'Cinco preguntas cortas, noventa segundos, y un número que parte de lo que hay en tu cuenta.',
    cta: 'Abrir TwoCoins',
    note: 'Treinta días gratis. Sin tarjeta para empezar: sólo tu correo.',
  },

  footer: {
    tagline: 'Una app de finanzas construida sobre cómo funciona el crédito aquí.',
    builtBy: 'Hecha por',
    columns: { product: 'Producto', legal: 'Legal', company: 'Empresa' },
    links: {
      how: 'Cómo funciona',
      desktop: 'Escritorio',
      gap: 'El problema',
      features: 'Funciones',
      pricing: 'Precios',
      faq: 'Preguntas',
      guides: 'Guías',
      openApp: 'Abrir TwoCoins',
      privacy: 'Aviso de Privacidad',
      terms: 'Términos del Servicio',
      cookies: 'Política de Cookies',
      deleteAccount: 'Eliminar tu cuenta',
      support: 'Soporte',
      contact: 'Contacto',
      instagram: 'Instagram',
      buildpassing: 'BuildPassing',
    },
    rights: 'Todos los derechos reservados.',
  },

  notFound: {
    title: 'Esta página no existe.',
    body: 'Puede que el enlace esté desactualizado o que la hayamos movido. La portada es un buen lugar para empezar.',
    cta: 'Volver a la portada',
  },

  guides: {
    title: 'Guías',
    intro:
      'Respuestas claras sobre cómo se mueve el dinero de verdad: de quincena a quincena, de corte a corte. Cada una explica primero, con las cuentas a la vista; de la app se habla en el último párrafo, si acaso.',
    read: 'Leer la guía',
    contents: 'En esta guía',
    trail: 'Estás aquí',
    updated: 'Actualizada',
    ctaTitle: 'Deja que la app lleve las cuentas',
    ctaBody:
      'TwoCoins hace esta cuenta cada quincena, con tus propios pagos y tarjetas. Treinta días gratis, sin tarjeta para empezar y nunca con la contraseña de tu banco.',
    ctaButton: 'Abrir TwoCoins',
    more: 'Más guías',
  },

  legal: {
    lastUpdated: 'Última actualización',
    effectiveDate: 'Fecha de entrada en vigor',
    contents: 'Contenido',
    questionsTitle: '¿Dudas sobre este documento?',
    questionsBody: 'Escríbenos y te responderá una persona.',
    bindingNotice:
      'Este documento también está disponible en inglés. Si vives en México, rige la versión en español; en cualquier otro lugar, la versión en inglés.',
  },
};

export default es;
